import {type ReactNode, forwardRef, memo} from 'react';
import {Check, Clock, X} from 'lucide-react';
import {cn} from '../lib/cn';

type IconVariant = 'clock-grey' | 'clock-blue' | 'tick-blue' | 'tick-green' | 'x-red';

interface StatusBadgeIconProps {
  variant: IconVariant;
  className?: string;
}

function StatusBadgeIcon({variant, className}: StatusBadgeIconProps) {
  const iconProps = {
    size: 10,
    className: cn('stroke-[1.5]', className),
  };

  switch (variant) {
    case 'clock-blue':
      return (
        <Clock
          {...iconProps}
          className={cn('text-[var(--chekin-color-brand-blue)]', className)}
        />
      );
    case 'tick-blue':
      return (
        <Check
          {...iconProps}
          className={cn('text-[var(--chekin-color-brand-blue)]', className)}
        />
      );
    case 'tick-green':
      return <Check {...iconProps} className={cn('text-emerald-600', className)} />;
    case 'x-red':
      return (
        <X
          {...iconProps}
          className={cn('text-[var(--chekin-color-brand-red)]', className)}
        />
      );
    case 'clock-grey':
    default:
      return (
        <Clock
          {...iconProps}
          className={cn('text-[var(--chekin-color-gray-2)]', className)}
        />
      );
  }
}

export type StatusBadgeVariant =
  | 'neutral'
  | 'progress-blue'
  | 'progress-blue-light'
  | 'progress-grey'
  | 'success-blue'
  | 'success-green'
  | 'error';

export interface StatusBadgeProps {
  children?: ReactNode;
  variant?: StatusBadgeVariant;
  showIcon?: boolean;
  textOnly?: boolean;
  iconOnly?: boolean;
  className?: string;
  disabled?: boolean;
}

const variantStyles: Record<
  StatusBadgeVariant,
  {
    container: string;
    text: string;
    icon: IconVariant;
  }
> = {
  neutral: {
    container: 'bg-[var(--chekin-color-surface-input-empty)]',
    text: 'text-[var(--chekin-color-gray-2)]',
    icon: 'clock-grey',
  },
  'progress-blue': {
    container: 'bg-[var(--chekin-color-surface-autocomplete)]',
    text: 'text-[var(--chekin-color-brand-blue)]',
    icon: 'clock-blue',
  },
  'progress-blue-light': {
    container: 'bg-[var(--chekin-color-surface-pressed)]',
    text: 'text-[var(--chekin-color-brand-blue)]',
    icon: 'clock-blue',
  },
  'progress-grey': {
    container: 'bg-[var(--chekin-color-surface-input-empty)]',
    text: 'text-[var(--chekin-color-gray-2)]',
    icon: 'clock-grey',
  },
  'success-blue': {
    container: 'bg-[var(--chekin-color-surface-autocomplete)]',
    text: 'text-[var(--chekin-color-brand-blue)]',
    icon: 'tick-blue',
  },
  'success-green': {
    container: 'bg-emerald-50',
    text: 'text-emerald-600',
    icon: 'tick-green',
  },
  error: {
    container: 'bg-red-50',
    text: 'text-[var(--chekin-color-brand-red)]',
    icon: 'x-red',
  },
};

const StatusBadgeInternal = forwardRef<HTMLDivElement, StatusBadgeProps>(
  (
    {
      children,
      variant = 'neutral',
      showIcon = true,
      textOnly = false,
      iconOnly = false,
      className,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const styles = variantStyles[variant];
    const hasText = !iconOnly && (children || !textOnly);
    const hasIcon = !textOnly && showIcon;

    const padding = (() => {
      if (iconOnly) return 'px-1 py-0';
      if (textOnly) return 'px-3 py-1';
      if (hasText && hasIcon) return 'py-1 pl-3 pr-2';
      return 'px-3 py-1';
    })();

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex cursor-default items-center justify-center gap-1 rounded-3xl text-[14px] font-medium leading-4',
          styles.container,
          styles.text,
          padding,
          disabled && 'opacity-50',
          className,
        )}
        {...props}
      >
        {hasIcon && (
          <StatusBadgeIcon variant={styles.icon} className="size-4 flex-shrink-0" />
        )}
        {hasText && <span className="whitespace-nowrap">{children}</span>}
      </div>
    );
  },
);

StatusBadgeInternal.displayName = 'StatusBadge';

export const StatusBadge = memo(StatusBadgeInternal);
